import { useEffect, useRef, useState, useId } from "react";
import { usePayPalPayment } from "@/hooks/usePayPalPayment";
import { Skeleton } from "@/components/ui/skeleton";

interface PayPalButtonProps {
  plan: "basic" | "premium";
  onSuccess?: () => void;
}

export function PayPalButton({ plan, onSuccess }: PayPalButtonProps) {
  const { renderButton, isScriptLoading } = usePayPalPayment();
  const [isRendering, setIsRendering] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId();
  const containerId = `paypal-button-${plan}-${uniqueId.replace(/:/g, '')}`;
  const renderedRef = useRef(false);

  useEffect(() => {
    if (renderedRef.current) return;

    let mounted = true;

    const render = async () => {
      if (!mounted) return;
      setIsRendering(true);
      await renderButton(containerId, plan, onSuccess);
      if (mounted) {
        setIsRendering(false);
        renderedRef.current = true;
      }
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(render, 100);
    
    return () => {
      mounted = false;
      clearTimeout(timeout);
      // Clear the container on unmount to prevent duplicates
      const container = document.getElementById(containerId);
      if (container) {
        container.innerHTML = "";
      }
      renderedRef.current = false;
    };
  }, [containerId, plan]); // Remove onSuccess and renderButton from deps

  return (
    <div className="w-full min-h-[45px]">
      {(isScriptLoading || isRendering) && !renderedRef.current && (
        <Skeleton className="h-[45px] w-full rounded-md" />
      )}
      <div 
        id={containerId} 
        ref={containerRef}
        className={isRendering && !renderedRef.current ? "hidden" : ""}
      />
    </div>
  );
}
